import { useMutation } from "@apollo/client";
import { isEqual, range } from "lodash";
import { useCallback, useState } from "react";
import {
  SaveOrgTalentIndicatorMutation,
  SaveOrgTalentIndicatorMutationVariables,
  TalentIndicatorNode,
} from "types/graphql-schema";

import updateOrgTalentIndicatorMutation from "@apps/org-settings/graphql/update-org-talent-indicator-mutation";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, successNotificationVar } from "@cache/cache";
import Button, { buttonTheme } from "@components/button/button";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import FormFields from "@components/form/form-fields";
import Input from "@components/input/input";
import Loading from "@components/loading/loading";
import Modal, { ModalWidth } from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { assertNonNull } from "@helpers/helpers";

export type TalentIndicator = Pick<
  TalentIndicatorNode,
  | "talentIndicatorType"
  | "name"
  | "customName"
  | "questionTitle"
  | "description"
  | "scaleStartsAt"
  | "scale"
  | "labels"
  | "labelDescriptions"
  | "hasAssessmentQuestions"
  | "hasLockedLabels"
>;

const inputClassName =
  "px-4 py-2 block sm:text-sm bg-white border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full disabled:bg-gray-100";
const textareaClassName =
  "resize-none px-4 py-2 h-12 border rounded-md text-sm w-full focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100";

enum TalentIndicatorLabelType {
  CUSTOM = "custom",
  AGREEMENT_5 = "agreement_5",
  AGREEMENT_7 = "agreement_7",
  YES_NO = "yes_no",
  LOW_HIGH = "low_high",
  EXPECTATIONS_3 = "expectations_3",
  EXPECTATION_4 = "expectations_4",
}
const defaultLabels: { [key in TalentIndicatorLabelType]: string[] | null } = {
  [TalentIndicatorLabelType.CUSTOM]: null,
  [TalentIndicatorLabelType.AGREEMENT_5]: [
    "Strongly Disagree",
    "Disagree",
    "Neutral",
    "Agree",
    "Strongly Agree",
  ],
  [TalentIndicatorLabelType.AGREEMENT_7]: [
    "Strongly Disagree",
    "Disagree",
    "Slightly Disagree",
    "Neutral",
    "Slightly Agree",
    "Agree",
    "Strongly Agree",
  ],
  [TalentIndicatorLabelType.YES_NO]: ["No", "Yes"],
  [TalentIndicatorLabelType.LOW_HIGH]: ["Low", "Medium", "High"],
  [TalentIndicatorLabelType.EXPECTATIONS_3]: [
    "Needs Improvement",
    "Meets Expectations",
    "Exceeds Expectations",
  ],
  [TalentIndicatorLabelType.EXPECTATION_4]: [
    "Needs Improvement",
    "Meets Expectations",
    "Exceeds Expectations",
    "Outstanding",
  ],
};

const scaleOptions = [
  {
    value: TalentIndicatorLabelType.CUSTOM,
    label: "Custom range",
  },
  {
    value: TalentIndicatorLabelType.AGREEMENT_5,
    label: "Strongly Disagree → Strongly Agree (1-5)",
  },
  {
    value: TalentIndicatorLabelType.AGREEMENT_7,
    label: "Strongly Disagree → Strongly Agree (1-7)",
  },
  {
    value: TalentIndicatorLabelType.YES_NO,
    label: "No → Yes (1-2)",
  },
  {
    value: TalentIndicatorLabelType.LOW_HIGH,
    label: "Low → High (1-3)",
  },
  {
    value: TalentIndicatorLabelType.EXPECTATIONS_3,
    label: "Needs Improvement → Exceeds Expectations (1-3)",
  },
  {
    value: TalentIndicatorLabelType.EXPECTATION_4,
    label: "Needs Improvement → Outstanding (1-4)",
  },
];

const getLabelType = (
  talentIndicator: TalentIndicator
): TalentIndicatorLabelType => {
  return Object.keys(defaultLabels).reduce((acc, key) => {
    if (
      isEqual(
        defaultLabels[key as TalentIndicatorLabelType],
        talentIndicator.labels
      ) &&
      isEqual(
        range(0, talentIndicator.labels?.length).map(() => ""),
        talentIndicator.labelDescriptions
      )
    ) {
      return key;
    }
    return acc;
  }, TalentIndicatorLabelType.CUSTOM) as TalentIndicatorLabelType;
};

const EditTalentIndicatorModal = ({
  talentIndicator,
  onClose,
  onTalentIndicatorUpdated,
}: {
  talentIndicator: TalentIndicator;
  onClose: () => void;
  onTalentIndicatorUpdated: () => void;
}) => {
  const label = useLabel();
  const currentOrganization = currentOrganizationVar();
  const [proposedTalentIndicator, setProposedTalentIndicator] =
    useState<TalentIndicator>(talentIndicator);
  const [labelType, setLabelType] = useState<TalentIndicatorLabelType>(
    getLabelType(talentIndicator)
  );
  const [isConfirming, setIsConfirming] = useState(false);
  const [saveTalentIndicator, { loading: isSaving }] = useMutation<
    SaveOrgTalentIndicatorMutation,
    SaveOrgTalentIndicatorMutationVariables
  >(updateOrgTalentIndicatorMutation);

  const handleSave = useCallback(async () => {
    saveTalentIndicator({
      variables: {
        organizationId: currentOrganization.id,
        talentIndicatorType: proposedTalentIndicator.talentIndicatorType,
        customName: proposedTalentIndicator.customName,
        questionTitle: proposedTalentIndicator.questionTitle,
        description: proposedTalentIndicator.description,
        scaleStartsAt: proposedTalentIndicator.scaleStartsAt,
        scale: proposedTalentIndicator.scale,
        labels: proposedTalentIndicator.labels,
        labelDescriptions: proposedTalentIndicator.labelDescriptions,
      },
      onCompleted: () => {
        onTalentIndicatorUpdated();
        successNotificationVar({
          title: `Indicator updated`,
        });
        onClose();
      },
    });
  }, [
    saveTalentIndicator,
    currentOrganization,
    proposedTalentIndicator,
    onTalentIndicatorUpdated,
    onClose,
  ]);

  const handleChangeLabelType = useCallback(
    (opt: ComboboxGenericOption<TalentIndicatorLabelType>) => {
      setLabelType(opt.value);
      const labels = defaultLabels[opt.value];
      if (!labels) {
        return;
      }
      setProposedTalentIndicator({
        ...proposedTalentIndicator,
        scaleStartsAt: 1,
        scale: labels.length,
        labels,
        labelDescriptions: range(0, labels.length).map(() => ""),
      });
    },
    [proposedTalentIndicator]
  );

  const handleChangeRangeValues = useCallback(
    (scaleStartsAt: number, scale: number) => {
      const newIndicator = {
        ...proposedTalentIndicator,
        scaleStartsAt,
        scale,
      };
      const labels = range(0, scale).map((x) => {
        return newIndicator.labels?.[x] || "";
      });
      const labelDescriptions = range(0, scale).map((x) => {
        return newIndicator.labelDescriptions?.[x] || "";
      });
      setProposedTalentIndicator({
        ...newIndicator,
        labels,
        labelDescriptions,
      });
    },
    [proposedTalentIndicator]
  );

  return (
    <Modal
      width={isConfirming ? ModalWidth.normal : ModalWidth.medium}
      open
      onClose={onClose}
      alignedTop
    >
      {isConfirming ? (
        <div className="p-6">
          <ModalTitle>Indicator Changes Confirmation</ModalTitle>
          <div className="mt-4">
            The trends in your reporting data may not be accurate if you choose
            to make modifications. Are you sure you want to make these changes?
          </div>
          <div className="mt-4 flex items-center gap-2 justify-end">
            <Button
              disabled={isSaving}
              theme={buttonTheme.text}
              onClick={onClose}
              text="Cancel"
            />
            <Button
              disabled={isSaving}
              theme={buttonTheme.primary}
              onClick={handleSave}
            >
              Confirm {isSaving && <Loading mini size="5" className="ml-2" />}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="p-6 border-b">
            <ModalTitle onClose={onClose}>
              {`Edit Indicator: ${talentIndicator.name}`}
            </ModalTitle>
            {talentIndicator.hasAssessmentQuestions && (
              <div className="mt-4 p-4 bg-amber-100 rounded-md flex gap-4 text-gray-800 text-sm">
                <TopicflowIcon type="info" className="h-5 w-5 text-gray-600" />
                <div>
                  <div className="font-semibold mb-1">
                    This indicator is already being used in{" "}
                    {label("review", { pluralize: true })}
                  </div>
                  <div>
                    Be careful when modifying this indicator because it has
                    already been used in a {label("review")}. The trends in your
                    reporting data may not be accurate if you choose to make
                    modifications.
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-6 border-b">
            <FormFields
              width={200}
              fields={[
                {
                  title: "Custom Name",
                  tooltip: `Modify this if your ${label(
                    "organization"
                  )} uses a different name for this indicator. This is the name we will use in reporting tables. By default we will use the Indicator Name.`,
                  render: () => (
                    <Input
                      placeholder="Add a custom name (optional)"
                      value={proposedTalentIndicator.customName ?? ""}
                      onChange={(e) =>
                        setProposedTalentIndicator({
                          ...proposedTalentIndicator,
                          customName: e.target.value,
                        })
                      }
                    />
                  ),
                },
                {
                  title: "Question Label",
                  tooltip: `This is what will show up on the question label when this indicator is used in a ${label(
                    "review"
                  )}. By default we will use the Indicator Name.`,
                  render: () => (
                    <Input
                      placeholder="Add a custom label (optional)"
                      value={proposedTalentIndicator.questionTitle ?? ""}
                      onChange={(e) =>
                        setProposedTalentIndicator({
                          ...proposedTalentIndicator,
                          questionTitle: e.target.value,
                        })
                      }
                    />
                  ),
                },
                {
                  title: "Description",
                  render: () => (
                    <TextareaWysiwyg
                      editable
                      placeholder="Add decription (optional)"
                      value={proposedTalentIndicator.description}
                      deps={[proposedTalentIndicator.customName]}
                      onChangeValue={(description: string) => {
                        setProposedTalentIndicator({
                          ...proposedTalentIndicator,
                          description,
                        });
                      }}
                    />
                  ),
                },
              ]}
            />
          </div>

          <div className="p-6 border-b">
            <FormFields
              width={200}
              fields={[
                {
                  title: "Scale",
                  render: () => (
                    <ComboboxGeneric
                      disabled={talentIndicator.hasLockedLabels}
                      options={scaleOptions}
                      onChangeValue={handleChangeLabelType}
                      value={assertNonNull(
                        scaleOptions.find((opt) => opt.value === labelType)
                      )}
                    />
                  ),
                },
                {
                  title: "Range",
                  render: () => (
                    <div className="flex gap-4 items-center">
                      <Input
                        className="w-18"
                        disabled={talentIndicator.hasLockedLabels}
                        type="number"
                        min={1}
                        step={1}
                        pattern="\d*"
                        value={proposedTalentIndicator.scaleStartsAt}
                        onChange={(evt) => {
                          setLabelType(TalentIndicatorLabelType.CUSTOM);
                          handleChangeRangeValues(
                            Number(evt.target.value),
                            proposedTalentIndicator.scale
                          );
                        }}
                      />
                      <TopicflowIcon
                        type="rightArrow"
                        className="text-gray-500 w-4 h-4 shrink-0"
                      />
                      <Input
                        className="w-18"
                        disabled={talentIndicator.hasLockedLabels}
                        type="number"
                        min={proposedTalentIndicator.scaleStartsAt + 1}
                        step={1}
                        pattern="\d*"
                        value={
                          proposedTalentIndicator.scaleStartsAt +
                          proposedTalentIndicator.scale -
                          1
                        }
                        onChange={(evt) => {
                          setLabelType(TalentIndicatorLabelType.CUSTOM);
                          handleChangeRangeValues(
                            proposedTalentIndicator.scaleStartsAt,
                            Number(evt.target.value)
                          );
                        }}
                      />
                    </div>
                  ),
                },
                ...range(0, proposedTalentIndicator.scale).map((index) => ({
                  title: `Label ${
                    proposedTalentIndicator.scaleStartsAt + index
                  }${
                    index === 0
                      ? " (Low)"
                      : index === proposedTalentIndicator.scale - 1
                      ? " (High)"
                      : ""
                  }`,
                  render: () => (
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        disabled={talentIndicator.hasLockedLabels}
                        value={proposedTalentIndicator.labels?.[index] ?? ""}
                        onChange={(evt) => {
                          setLabelType(TalentIndicatorLabelType.CUSTOM);
                          setProposedTalentIndicator({
                            ...proposedTalentIndicator,
                            labels: proposedTalentIndicator.labels?.map(
                              (label, i) =>
                                i === index ? evt.target.value : label
                            ),
                          });
                        }}
                        placeholder="Label (optional)"
                        className={inputClassName}
                      />
                      <textarea
                        value={
                          proposedTalentIndicator.labelDescriptions?.[index] ||
                          ""
                        }
                        disabled={talentIndicator.hasLockedLabels}
                        onChange={(evt) => {
                          setLabelType(TalentIndicatorLabelType.CUSTOM);
                          setProposedTalentIndicator({
                            ...proposedTalentIndicator,
                            labelDescriptions:
                              proposedTalentIndicator.labelDescriptions?.map(
                                (label, i) =>
                                  i === index ? evt.target.value : label
                              ),
                          });
                        }}
                        placeholder="Tooltip (optional)"
                        className={textareaClassName}
                      />
                    </div>
                  ),
                })),
              ]}
            />
          </div>

          <div className="p-6 flex items-center gap-2 justify-end">
            <Button
              disabled={isSaving}
              theme={buttonTheme.text}
              onClick={onClose}
              text="Cancel"
            />
            <Button
              disabled={isSaving}
              theme={buttonTheme.primary}
              onClick={() =>
                proposedTalentIndicator.hasAssessmentQuestions
                  ? setIsConfirming(true)
                  : handleSave()
              }
            >
              Save {isSaving && <Loading mini size="5" className="ml-2" />}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default EditTalentIndicatorModal;
